"use client";

import type { PhotoPrompt } from "@/app/directories/checkout/components/intake/consumerPhotosHelper";
import type {
  IntakeStepTypes,
  TypeformIntakeFormSection,
} from "@/app/directories/checkout/components/intake/intakeHelper";
import { getStartAtQuestionIndex } from "@/app/directories/checkout/components/intake/intakeHelper";
import type { IntakeData } from "@/app/directories/companion/components_v2/AppointmentForm/hooks/useIntakeForm";
import type { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import {
  type ReactNode,
  createContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRef } from "react";
import type { RefObject } from "react";
import type { IntakeFormSavedData } from "../Intake";
import type { IntakeFormData } from "../Intake";

export interface IntakeContextType {
  signature: string | undefined;
  setSignature: (signature: string) => void;
  prefillInfo: Map<number, IntakeFormSavedData>;
  intakeData: IntakeData[];
  intakeFormsData: IntakeFormData[];
  pcUpdatedRetrievableIntakeResponses;
  pcUpdatedRetrievableByFormSection;
  allQuestionsByFormSection;
  availableQuestionsByFormSection;
  totalQuestionsCount: number;
  formSectionsToComplete;
  areAllIntakeFormsCompleted: boolean;
  anyFormsUnavailable: boolean;
  isWaitingRoom: boolean;
  hiddenQuestions: Set<number>;
  setHiddenQuestions: React.Dispatch<React.SetStateAction<Set<number>>>;
  intakeStep: IntakeStepTypes;
  shouldStartAtStep: IntakeStepTypes;
  handleSectionChange: (index: number) => void;
  activeSection: TypeformIntakeFormSection | undefined;
  currentSectionIndex: number;
  editingFormSectionId: string | null;
  setEditingFormSectionId: (id: string | null) => void;
  startAtQuestionIndex: number;
  previousIntakeStep: RefObject<IntakeStepTypes>;
  previousSectionNumber: RefObject<number>;
  handleSetIntakeStep: (step: IntakeStepTypes) => void;
  userLocale: LanguageCode;
  hasStartedIntake: boolean;
  resources;
  isVirtualVisit: boolean;
  consumerPhotoPrompts: PhotoPrompt[];
  setConsumerPhotoPrompts: (photos: PhotoPrompt[]) => void;
  photoIndex: number;
  setPhotoIndex: (index: number) => void;
  previousPhotoIndexRef: RefObject<number>;
  photoFileToUpload: File | null;
  setPhotoFileToUpload: (file: File | null) => void;
  consumerZip: string;
  consumerFullName: string;
  orgId: number;
}

export const IntakeContext = createContext<IntakeContextType | undefined>(
  undefined,
);

export function IntakeProvider({
  props,
  children,
}: { props; children: ReactNode }) {
  const {
    prefillInfo,
    intakeData,
    intakeFormsData,
    pcUpdatedRetrievableIntakeResponses,
    pcUpdatedRetrievableByFormSection,
    allQuestionsByFormSection,
    availableQuestionsByFormSection,
    totalQuestionsCount,
    formSectionsToComplete,
    areAllIntakeFormsCompleted,
    anyFormsUnavailable,
    isWaitingRoom,
    userLocale,
    hasStartedIntake,
    resources,
    consumerPhotosToPrompt,
    isVirtualVisit,
    consumerZip,
    consumerFullName,
    orgId,
  } = props;
  const [signature, setSignature] = useState<string | undefined>(undefined);

  const [hiddenQuestions, setHiddenQuestions] = useState<Set<number>>(
    new Set(),
  );

  const [photoFileToUpload, setPhotoFileToUpload] = useState<File | null>(null);

  const [consumerPhotoPrompts, setConsumerPhotoPrompts] = useState<
    PhotoPrompt[]
  >(consumerPhotosToPrompt);

  // Determine the initial step based on intake data
  const shouldStartAtStep = useMemo((): IntakeStepTypes => {
    if (
      pcUpdatedRetrievableByFormSection.length === 0 &&
      availableQuestionsByFormSection.length > 0
    ) {
      if (consumerPhotoPrompts.length > 0) {
        return "photos";
      }
      return "questions";
    }
    if (pcUpdatedRetrievableByFormSection.length > 0) {
      return "review";
    }
    return "redirecting"; // fallback - will navigate away in component
  }, [
    pcUpdatedRetrievableByFormSection.length,
    availableQuestionsByFormSection.length,
    consumerPhotoPrompts.length,
  ]);

  const [intakeStep, setIntakeStep] =
    useState<IntakeStepTypes>(shouldStartAtStep);

  const previousIntakeStep = useRef<IntakeStepTypes>("review");
  const previousSectionNumber = useRef(0);
  const hasInitializedStep = useRef(false);

  const [photoIndex, setPhotoIndex] = useState(0);
  const previousPhotoIndexRef = useRef(photoIndex);

  // Only update step from shouldStartAtStep on initial mount
  // This prevents resetting the step when user is actively filling out the form
  // (e.g., when saving a pharmacy response updates pcUpdatedRetrievableByFormSection)
  useEffect(() => {
    if (!hasInitializedStep.current) {
      setIntakeStep(shouldStartAtStep);
      hasInitializedStep.current = true;
    }
  }, [shouldStartAtStep]);

  // only want this when prefillInfo changes
  const startAtQuestionIndex = useMemo(
    () =>
      getStartAtQuestionIndex(
        formSectionsToComplete,
        prefillInfo,
        hiddenQuestions,
      ),
    [prefillInfo, hiddenQuestions, formSectionsToComplete],
  );

  const [editingFormSectionId, setEditingFormSectionId] = useState<
    string | null
  >(
    formSectionsToComplete.length > 0
      ? startAtQuestionIndex === -1
        ? null
        : formSectionsToComplete[startAtQuestionIndex].sectionId
      : null,
  );

  const activeSection = availableQuestionsByFormSection.find(
    (section) => section.sectionId === editingFormSectionId,
  );

  // Derive current section index from editingFormSectionId
  const currentSectionIndex = useMemo(() => {
    if (!editingFormSectionId) return 0;
    return formSectionsToComplete.findIndex(
      (section) => section.sectionId === editingFormSectionId,
    );
  }, [editingFormSectionId, formSectionsToComplete]);

  const handleSectionChange = (index: number) => {
    if (index === -1) {
      console.error("not sure where to jump");
      //setEditingFormSectionId(formSectionsToComplete[0].sectionId);
      return;
    }
    setEditingFormSectionId(formSectionsToComplete[index].sectionId);
  };

  const handleSetIntakeStep = (step: IntakeStepTypes) => {
    previousIntakeStep.current = intakeStep;
    setIntakeStep(step);
  };

  return (
    <IntakeContext.Provider
      value={{
        signature,
        setSignature,
        prefillInfo,
        intakeData,
        intakeFormsData,
        pcUpdatedRetrievableIntakeResponses,
        pcUpdatedRetrievableByFormSection,
        allQuestionsByFormSection,
        availableQuestionsByFormSection,
        totalQuestionsCount,
        formSectionsToComplete,
        areAllIntakeFormsCompleted,
        anyFormsUnavailable,
        isWaitingRoom,
        hiddenQuestions,
        setHiddenQuestions,
        intakeStep,
        shouldStartAtStep,
        handleSectionChange,
        activeSection,
        currentSectionIndex,
        editingFormSectionId,
        setEditingFormSectionId,
        startAtQuestionIndex,
        previousIntakeStep,
        previousSectionNumber,
        handleSetIntakeStep,
        userLocale,
        hasStartedIntake,
        resources,
        consumerPhotoPrompts,
        setConsumerPhotoPrompts,
        photoIndex,
        setPhotoIndex,
        previousPhotoIndexRef,
        photoFileToUpload,
        setPhotoFileToUpload,
        isVirtualVisit,
        consumerZip,
        consumerFullName,
        orgId,
      }}
    >
      {children}
    </IntakeContext.Provider>
  );
}
